import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  PoPageModule,
  PoPageAction,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoPageFilter,
  PoLoadingModule,
  PoNotificationService,
  PoTagModule,
  PoPageSlideModule,
  PoPageSlideComponent,
  PoFieldModule,
  PoButtonModule,
  PoSelectOption,
  PoModalModule,
  PoModalComponent,
  PoModalAction
} from '@po-ui/ng-components';

import { NotaEntregue, NotasEntreguesFiltros, NotasEntreguesService } from './notas-entregue.service';
import { LoginService } from '../../core/services/login.service';
import { FilialService } from '../../core/services/filial.service';
import { limparNumeroNf } from '../../shared/numero-utils';
import { AnexosService, AnexoParaPreparar, AnexoTipo, gedIndisponivelMsg } from '../nota-detalhe/anexos.service';

type NotaRow = NotaEntregue & { numeroExibe?: string };

// Slots de anexo da modal de reanexo - mesmo padrao (DAN/BOL/ROM) do
// executar-lote/notas-adicionar.
type ReanexoSlot = 'danfe' | 'boleto' | 'romaneio';

@Component({
  selector: 'app-notas-entregue',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PoPageModule,
    PoTableModule,
    PoLoadingModule,
    PoTagModule,
    PoPageSlideModule,
    PoFieldModule,
    PoButtonModule,
    PoModalModule
  ],
  templateUrl: './notas-entregue.html',
  styleUrls: ['./notas-entregue.css'],
})
export class NotasEntregueComponent implements OnInit {

  @ViewChild('slideFilterAdvanced') slideFilterAdvanced!: PoPageSlideComponent;
  @ViewChild('reprovarModal') reprovarModal!: PoModalComponent;
  @ViewChild('reanexoModal') reanexoModal!: PoModalComponent;
  @ViewChild('reprocCnxModal') reprocCnxModal!: PoModalComponent;

  isLoading = false;
  reprovando = false;

  // Estado da modal de reprovacao
  notaParaReprovar: NotaEntregue | null = null;
  motivoReprovacao = '';

  // Permissao p/ reprovar (privilegio REPROVA da rota /notas-entregues).
  // Sem o priv, a acao da tabela some.
  canReprovar = false;

  // Permissao p/ anexar documentos (privilegio FILE da rota /notas-list,
  // mesmo auth exigido pelo backend em anexos.tlpp). Independente da
  // permissao de reprovar.
  canAnexar = false;

  // Permissao p/ reenviar a nota de servico ao Conexos (privilegio REPRCNX
  // da rota /notas-entregues). Sem o priv, a acao da tabela some.
  canReprocCnx = false;

  // Estado da modal de reprocesso no Conexos
  notaReprocCnx: NotaRow | null = null;
  reprocessandoCnx = false;
  reprocCnxResultado = '';
  reprocCnxOk = false;

  readonly modalReprocCnxConfirma: PoModalAction = {
    label: 'Reenviar ao Conexos',
    action: () => this.confirmarReprocCnx()
  };
  readonly modalReprocCnxCancela: PoModalAction = {
    label: 'Fechar',
    action: () => this.fecharReprocCnx()
  };

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Atualizar', action: () => this.carregar(), icon: 'an an-arrows-clockwise' }
  ];

  // Acoes por linha da tabela. A acao "Reprovar" so' aparece quando o
  // usuario tem o privilegio REPROVA (controlado em ngOnInit/constructor).
  tableActions: Array<PoTableAction> = [];

  // Acoes da modal de confirmacao
  readonly modalReprovarConfirma: PoModalAction = {
    label: 'Confirmar reprovacao',
    action: () => this.confirmarReprovar(),
    danger: true
  };
  readonly modalReprovarCancela: PoModalAction = {
    label: 'Cancelar',
    action: () => this.fecharReprovar()
  };

  // Estado da modal de reanexo.
  notaReanexo: NotaEntregue | null = null;
  reanexoGedFolder = '';
  reanexoGedOptions: PoSelectOption[] = [];
  reanexoDanfe: File | null = null;
  reanexoBoleto: File | null = null;
  reanexoRomaneio: File | null = null;
  reanexoEnviando = false;
  // Flags de drag-over por slot (feedback visual do drag-and-drop, mesmo
  // padrao do executar-lote).
  reanexoDanfeDrag = false;
  reanexoBoletoDrag = false;
  reanexoRomaneioDrag = false;

  readonly modalReanexoConfirma: PoModalAction = {
    label: 'Enviar anexos',
    action: () => this.confirmarReanexo()
  };
  readonly modalReanexoCancela: PoModalAction = {
    label: 'Cancelar',
    action: () => this.fecharReanexo()
  };

  readonly filterSettings: PoPageFilter = {
    action: this.filterAction.bind(this),
    placeholder: 'Pesquisar notas...'
  };

  readonly tipoOptions: PoSelectOption[] = [
    { label: 'NFSe', value: 'NFSe' },
    { label: 'CTe',  value: 'CTe'  },
    { label: 'NFe',  value: 'NFe'  }
  ];

  readonly statusOptions: PoSelectOption[] = [
    { label: 'Aprovado', value: 'Aprovado' },
    { label: 'Pendente', value: 'Pendente' },
    { label: 'Recusado', value: 'Recusado' }
  ];

  readonly origemOptions: PoSelectOption[] = [
    { label: 'Arquivei',  value: 'Arquivei' },
    { label: 'Manual',    value: 'Manual' },
    { label: 'Transmite', value: 'Transmite' }
  ];

  readonly ocultoOptions: PoSelectOption[] = [
    { label: 'Sim', value: 'Sim' },
    { label: 'Não', value: 'Nao' }
  ];

  readonly finOptions: PoSelectOption[] = [
    { label: 'Sim', value: 'S' },
    { label: 'Não', value: 'N' }
  ];

  readonly semAnexoOptions: PoSelectOption[] = [
    { label: 'Todas',      value: ''  },
    { label: 'Sem anexo',  value: 'S' }
  ];

  // Filtro por situacao da integracao com o Conexos. Multiselect: o valor vai
  // ao backend como lista separada por virgula (ex.: '4,5' = so as em erro).
  readonly cnxStatusOptions: PoSelectOption[] = [
    { label: 'Aguardando envio',          value: '1' },
    { label: 'Criada, falta finalizar',   value: '2' },
    { label: 'Integrada',                 value: '3' },
    { label: 'Em erro, tentando de novo', value: '4' },
    { label: 'Aguardando reprocesso',     value: '5' },
    { label: 'Fora do escopo',            value: '9' }
  ];

  // Tomadores (mesma lista do select da inclusao manual). value=CNPJ do
  // tomador; convertido para codigo da filial Protheus no confirmAdvancedFilter
  // via FilialService.codigoByCnpj antes de bater no backend.
  tomadorOptions: PoSelectOption[] = [];

  // Opcoes do filtro "Usuario" - carregadas de GET /portalnf/usuarios (ZOA).
  // value = ZOA_COD (casa com F1_ZUSRPN no filtro do backend); label = nome.
  usuarioOptions: PoSelectOption[] = [];

  filterForm: FormGroup;

  columns: Array<PoTableColumn> = [
    { property: 'numeroExibe', label: 'Número', type: 'link', action: (value: string, row: NotaRow) => this.openDetalhe(row) },
    { property: 'numeroRps', label: 'Nº RPS', width: '130px' },
    // Serie da nota efetivada: com o escalonamento do MV_PNFSER (S1 -> S2 ->
    // S3) o mesmo numero pode existir em series diferentes, entao a coluna
    // distingue as entregas.
    { property: 'serie', label: 'Série', width: '80px' },
    { property: 'tipo', label: 'Tipo', width: '90px' },
    { property: 'fornecedor', label: 'Fornecedor', width: '300px' },
    { property: 'processo', label: 'Processo', width: '160px' },
    { property: 'status', label: 'Status', type: 'label', width: '120px', labels: [
      { value: 'Aprovado', color: 'color-10', label: 'Aprovado' }
    ]},
    // Situacao da integracao da NFS-e com o Conexos (job PnfNfsJob). Vazio =
    // nota fora do ciclo: nao e nota de servico, filial nao habilitada ou
    // campos de controle ainda nao criados no dicionario.
    { property: 'cnxStatus', label: 'Conexos', type: 'label', width: '190px', labels: [
      { value: '1', color: 'color-08', label: 'Aguardando envio' },
      { value: '2', color: 'color-08', label: 'Criada, falta finalizar' },
      { value: '3', color: 'color-10', label: 'Integrada' },
      { value: '4', color: 'color-07', label: 'Em erro, tentando de novo' },
      { value: '5', color: 'color-07', label: 'Aguardando reprocesso' },
      { value: '9', color: 'color-09', label: 'Fora do escopo' }
    ]},
    { property: 'cnxDoc', label: 'Doc. Conexos', width: '130px' },
    { property: 'total', label: 'Total', type: 'currency', format: 'BRL', width: '140px' },
    { property: 'emissao', label: 'Emissão', type: 'date', width: '120px' },
    { property: 'recebida', label: 'Recebida', type: 'date', width: '120px' },
    { property: 'aprovado', label: 'Aprovado', type: 'date', width: '120px' },
    { property: 'vencimento', label: 'Vencimento', type: 'date', width: '120px' },
    { property: 'usuario', label: 'Usuário' },
    { property: 'codigo', label: 'Código', width: '100px' },
    { property: 'tes', label: 'TES', width: '70px' },
    { property: 'produto', label: 'Produto' },
    { property: 'fin', label: 'FIN', width: '60px' },
    { property: 'origem', label: 'Origem', width: '110px', type: 'label', labels: [
      { value: 'Arquivei',  color: 'color-11', label: 'Arquivei' },
      { value: 'Manual',    color: 'color-01', label: 'Manual' },
      { value: 'Transmite', color: 'color-08', label: 'Transmite' }
    ]},
    { property: 'oculto', label: 'Oculto', width: '80px' },
    {
      property: 'qtdAnexos',
      label: 'Anexos',
      type: 'label',
      width: '90px',
      labels: [
        { value: 0, color: 'color-07', label: 'Sem anexo' }
      ]
    }
  ];

  notas: NotaRow[] = [];
  notasFiltered: NotaRow[] = [];
  hasActiveFilters = false;
  loadingMore = false;
  page = 1;
  readonly pageSize = 10;
  hasMore = false;
  // Filtros server-side (advanced filter slide). A barra de busca rapida
  // (filterSettings.action) continua client-side em cima de this.notas.
  private filtrosAtivos: NotasEntreguesFiltros = {};

  get tableHeight(): number {
    return Math.max(window.innerHeight - 320, 300);
  }

  // Sai true quando o usuario tem o privilegio OCULTO na rota
  // /notas-entregues. Controla o filtro 'Oculto' e a coluna da tabela.
  canViewOcultos = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private notif: PoNotificationService,
    private service: NotasEntreguesService,
    private loginService: LoginService,
    private filialService: FilialService,
    private anexosService: AnexosService
  ) {
    this.filterForm = this.buildFilterForm();
    this.filterForm.valueChanges.subscribe(values => {
      this.hasActiveFilters = Object.values(values).some(v => v !== null && v !== '');
    });
    this.canViewOcultos = this.loginService.hasPrivilegeByRota('/notas-entregues', 'OCULTO');
    if (!this.canViewOcultos) {
      this.columns = this.columns.filter(c => c.property !== 'oculto');
    }

    this.canReprovar = this.loginService.hasPrivilegeByRota('/notas-entregues', 'REPROVA');
    // Permissao de anexo: espelha o auth do backend em anexos.tlpp:184
    // (self:auth("/notas-list", "FILE")).
    this.canAnexar = this.loginService.hasPrivilegeByRota('/notas-list', 'FILE');
    // Reprocesso da integracao com o Conexos: mesmo privilegio exigido pelo
    // backend em notasEntregues.tlpp (auth "/notas-entregues", "REPRCNX").
    this.canReprocCnx = this.loginService.hasPrivilegeByRota('/notas-entregues', 'REPRCNX');

    const acoes: PoTableAction[] = [];
    if (this.canAnexar) {
      acoes.push({
        label: 'Anexar documentos',
        icon: 'an an-paperclip',
        action: (row: NotaEntregue) => this.abrirReanexo(row)
      });
    }
    if (this.canReprocCnx) {
      acoes.push({
        label: 'Reprocessar no Conexos',
        icon: 'an an-arrows-clockwise',
        action: (row: NotaRow) => this.abrirReprocCnx(row),
        visible: (row: NotaRow) => this.podeReprocCnx(row)
      });
    }
    if (this.canReprovar) {
      acoes.push({
        label: 'Reprovar',
        icon: 'an an-x-circle',
        type: 'danger',
        action: (row: NotaEntregue) => this.abrirReprovar(row)
      });
    }
    this.tableActions = acoes;
  }

  ngOnInit(): void {
    this.carregar();
    // Lista de tomadores (= filiais Protheus). Mesma fonte usada na
    // inclusao manual; cache compartilhado no FilialService.
    this.filialService.filiais$.subscribe(list => {
      this.tomadorOptions = list.map(f => ({
        label: (f.cnpj ? f.cnpj + ' - ' : '') + f.nome,
        value: f.cnpj
      }));
    });
    this.filialService.loadAll().subscribe({
      error: () => this.notif.warning('Nao foi possivel carregar a lista de tomadores.')
    });

    // Usuarios do portal pro multiselect do filtro "Usuario". Mostra so o
    // nome (label); value = ZOA_COD (casa com F1_ZUSRPN no backend).
    this.service.listarUsuarios().subscribe({
      next: list => {
        this.usuarioOptions = list.map(u => ({
          label: u.nome,
          value: u.cod
        }));
      },
      error: () => this.notif.warning('Nao foi possivel carregar a lista de usuarios.')
    });
  }

  // Numero exibido: se a NF-e (numeroNfe = F1_NFELETR) estiver preenchida,
  // exibe ela; senao o F1_DOC (numero). Mesma regra de notas-list. numeroExibe
  // e' SO exibicao; openDetalhe continua usando row.numero (chave da nota).
  private toRows(data: NotaEntregue[]): NotaRow[] {
    return data.map(n => {
      const numeroExibe = limparNumeroNf(n.numeroNfe || n.numero || '');
      return { ...n, numeroExibe };
    });
  }

  carregar(): void {
    this.page = 1;
    this.notas = [];
    this.notasFiltered = [];
    this.hasMore = false;
    this.isLoading = true;
    this.service.list(this.page, this.pageSize, this.filtrosAtivos).subscribe({
      next: res => {
        this.notas = this.toRows(res.data);
        this.notasFiltered = [...this.notas];
        this.hasMore = res.hasNext;
        this.isLoading = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar notas entregues.');
        this.isLoading = false;
      }
    });
  }

  carregarMais(): void {
    if (!this.hasMore || this.loadingMore) return;
    this.loadingMore = true;
    this.page++;
    this.service.list(this.page, this.pageSize, this.filtrosAtivos).subscribe({
      next: res => {
        this.notas = [...this.notas, ...this.toRows(res.data)];
        this.notasFiltered = [...this.notas];
        this.hasMore = res.hasNext;
        this.loadingMore = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar mais notas.');
        this.loadingMore = false;
        this.page--;
      }
    });
  }

  // -------------------------------------------------------------------
  // Reprovacao (excluir SF1+SD1 via MATA103 op 5)
  // -------------------------------------------------------------------

  abrirReprovar(nota: NotaEntregue): void {
    this.notaParaReprovar = nota;
    this.motivoReprovacao = '';
    this.reprovarModal.open();
  }

  fecharReprovar(): void {
    this.reprovarModal.close();
    this.notaParaReprovar = null;
    this.motivoReprovacao = '';
  }

  confirmarReprovar(): void {
    if (!this.notaParaReprovar || this.reprovando) { return; }
    const n = this.notaParaReprovar;

    this.reprovando = true;
    this.service.reprovar({
      filial: n.filial,
      doc:    n.numero,
      serie:  n.serie,
      fornec: n.fornecedorCod,
      loja:   n.fornecedorLoja,
      motivo: this.motivoReprovacao || undefined
    }).subscribe({
      next: res => {
        this.reprovando = false;
        this.notif.success(res.message ?? `Nota ${n.numero} reprovada com sucesso.`);
        // Remove a nota da listagem em memoria (evita reload completo)
        this.notas = this.notas.filter(x => x !== n);
        this.notasFiltered = this.notasFiltered.filter(x => x !== n);
        this.fecharReprovar();
      },
      error: err => {
        this.reprovando = false;
        const msg = err?.error?.message ?? 'Erro ao reprovar a nota.';
        this.notif.error(msg);
      }
    });
  }

  // -------------------------------------------------------------------
  // Reprocesso da integracao da NFS-e com o Conexos
  // -------------------------------------------------------------------

  // So oferece o reprocesso para nota de servico que ainda nao foi integrada.
  // Nota ja integrada tem documento no Conexos: reenviar duplicaria.
  podeReprocCnx(row: NotaRow): boolean {
    if (!row || row.tipo !== 'NFSe') { return false; }
    return (row.cnxStatus ?? '') !== '3';
  }

  abrirReprocCnx(nota: NotaRow): void {
    this.notaReprocCnx = nota;
    this.reprocCnxResultado = '';
    this.reprocCnxOk = false;
    this.reprocCnxModal.open();
  }

  fecharReprocCnx(): void {
    this.reprocCnxModal.close();
    this.notaReprocCnx = null;
    this.reprocCnxResultado = '';
  }

  confirmarReprocCnx(): void {
    if (!this.notaReprocCnx || this.reprocessandoCnx) { return; }
    const n = this.notaReprocCnx;

    this.reprocessandoCnx = true;
    this.reprocCnxResultado = '';
    this.service.reprocessarConexos({
      filial: n.filial,
      doc:    n.numero,
      serie:  n.serie,
      fornec: n.fornecedorCod,
      loja:   n.fornecedorLoja
    }).subscribe({
      next: res => {
        this.reprocessandoCnx = false;
        this.reprocCnxOk = !!res.success;
        this.reprocCnxResultado = res.message ?? '';
        // Espelha o novo estado na linha (evita recarregar a listagem toda).
        n.cnxStatus    = res.status ?? n.cnxStatus;
        n.cnxStatusDes = res.statusDes ?? n.cnxStatusDes;
        n.cnxDoc       = res.docCod || n.cnxDoc;
        n.cnxMsg       = res.success ? '' : (res.message ?? n.cnxMsg);
        if (res.success) {
          this.notif.success(res.message ?? `Nota ${n.numero} reenviada ao Conexos.`);
        } else {
          this.notif.warning(res.message ?? 'A nota nao pode ser integrada.');
        }
      },
      error: err => {
        this.reprocessandoCnx = false;
        this.reprocCnxOk = false;
        const msg = err?.error?.message ?? 'Erro ao reprocessar a nota no Conexos.';
        this.reprocCnxResultado = msg;
        this.notif.error(msg);
      }
    });
  }

  // -------------------------------------------------------------------
  // Reanexo (upload de documentos em nota ja entregue, sem anexo por
  // falha do upload na aprovacao)
  // -------------------------------------------------------------------

  abrirReanexo(nota: NotaEntregue): void {
    this.notaReanexo     = nota;
    this.reanexoDanfe    = null;
    this.reanexoBoleto   = null;
    this.reanexoRomaneio = null;
    this.reanexoDanfeDrag    = false;
    this.reanexoBoletoDrag   = false;
    this.reanexoRomaneioDrag = false;
    this.reanexoGedFolder = '';
    this.anexosService.gedFolders().subscribe({
      next: (folders) => {
        this.reanexoGedOptions = folders.map(f => ({ label: f, value: f }));
        // Sugere a primeira opcao (default do MV_PNFGDIR) mas deixa editar:
        // a pasta usada na aprovacao nao e gravada na SF1.
        if (folders.length > 0) { this.reanexoGedFolder = folders[0]; }
      },
      error: () => this.notif.warning('Nao foi possivel carregar as pastas do GED.')
    });
    this.reanexoModal.open();
  }

  fecharReanexo(): void {
    this.notaReanexo = null;
    this.reanexoModal.close();
  }

  onReanexoFile(slot: ReanexoSlot, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0] ?? null;
    if (file) { this.assignReanexoFile(slot, file); }
    input.value = '';
  }

  onReanexoSlotDragOver(event: DragEvent, slot: ReanexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setReanexoDragFlag(slot, true);
  }

  onReanexoSlotDragLeave(event: DragEvent, slot: ReanexoSlot): void {
    event.preventDefault();
    this.setReanexoDragFlag(slot, false);
  }

  onReanexoSlotDrop(event: DragEvent, slot: ReanexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setReanexoDragFlag(slot, false);
    const file = event.dataTransfer?.files?.[0] ?? null;
    if (file) { this.assignReanexoFile(slot, file); }
  }

  clearReanexoFile(slot: ReanexoSlot): void {
    if (slot === 'danfe')    { this.reanexoDanfe = null; }
    if (slot === 'boleto')   { this.reanexoBoleto = null; }
    if (slot === 'romaneio') { this.reanexoRomaneio = null; }
  }

  private assignReanexoFile(slot: ReanexoSlot, file: File): void {
    if (file.size > AnexosService.MAX_BYTES) {
      this.notif.warning(`${file.name}: excede 10 MB.`);
      return;
    }
    if (slot === 'danfe')    { this.reanexoDanfe = file; }
    if (slot === 'boleto')   { this.reanexoBoleto = file; }
    if (slot === 'romaneio') { this.reanexoRomaneio = file; }
  }

  private setReanexoDragFlag(slot: ReanexoSlot, value: boolean): void {
    if (slot === 'danfe')    { this.reanexoDanfeDrag = value; }
    if (slot === 'boleto')   { this.reanexoBoletoDrag = value; }
    if (slot === 'romaneio') { this.reanexoRomaneioDrag = value; }
  }

  private async confirmarReanexo(): Promise<void> {
    const nota = this.notaReanexo;
    if (!nota || this.reanexoEnviando) { return; }

    const itens: AnexoParaPreparar[] = [];
    const tag = `NF ${nota.numero}`;
    if (this.reanexoDanfe) {
      const tipoDoc: AnexoTipo = nota.tipo === 'CTe' ? 'CTE' : 'DAN';
      itens.push({ tipo: tipoDoc, file: this.reanexoDanfe, tag });
    }
    if (this.reanexoBoleto)   { itens.push({ tipo: 'BOL', file: this.reanexoBoleto,   tag }); }
    if (this.reanexoRomaneio) { itens.push({ tipo: 'ROM', file: this.reanexoRomaneio, tag }); }

    if (itens.length === 0) {
      this.notif.warning('Selecione ao menos um arquivo.');
      return;
    }
    if (!this.reanexoGedFolder) {
      this.notif.warning('Selecione a pasta do GED.');
      return;
    }

    this.reanexoEnviando = true;
    try {
      // Gate GED: pasta inexistente no Conexos bloqueia o envio (mesma regra do lote).
      if (nota.processo) {
        let check;
        try {
          check = await firstValueFrom(this.anexosService.gedFolderExists({
            filOri: nota.filial, processo: nota.processo, folder: this.reanexoGedFolder
          }));
        } catch {
          check = { exists: false, gedStatus: 'OFFLINE' as const };
        }
        // Checagem inconclusiva (login recusado / GED fora do ar): reportar o
        // acesso, nao "pasta inexistente" - a pasta provavelmente esta la.
        if (check.gedStatus !== 'OK') {
          this.notif.error(gedIndisponivelMsg(check.gedStatus)!);
          return;
        }
        if (!check.exists) {
          this.notif.warning(
            `Pasta GED "${this.reanexoGedFolder}" nao existe no Conexos para o processo ` +
            `${nota.processo}. Crie a pasta antes de anexar.`
          );
          return;
        }
      }

      // Le os arquivos ANTES de enviar - mesma disciplina do lote.
      const prep = await this.anexosService.prepararAnexos(itens);
      if (prep.erros.length > 0) {
        this.notif.error(prep.erros.join(' | '));
        return;
      }

      let ok = 0;
      const falhas: string[] = [];
      for (const a of prep.preparados) {
        try {
          await firstValueFrom(this.anexosService.upload({
            chave: {
              filOri: nota.filial,
              numero: nota.numero,
              serie:  nota.serie,
              fornec: nota.fornecedorCod,
              loja:   nota.fornecedorLoja
            },
            tipo:         a.tipo,
            filename:     a.filename,
            contentType:  a.contentType,
            base64:       a.base64,
            gedFolder:    this.reanexoGedFolder,
            dtVencimento: nota.vencimento || undefined
          }));
          ok++;
        } catch (err: any) {
          falhas.push(`${a.tipo}: ${err?.error?.message ?? err?.message ?? 'falha upload'}`);
        }
      }

      if (falhas.length === 0) {
        this.notif.success(`${ok} anexo(s) enviado(s).`);
        this.fecharReanexo();
        this.carregar();
      } else {
        this.notif.error(`${ok} OK, ${falhas.length} falha(s): ${falhas.join(' | ')}`);
      }
    } finally {
      this.reanexoEnviando = false;
    }
  }

  openDetalhe(nota: NotaEntregue): void {
    // Abre o detalhe numa nova aba pra preservar a listagem (filtros + scroll)
    // ao voltar. Como nao usamos router.navigate, montamos a URL via UrlTree.
    const tree = this.router.createUrlTree(['/nota-detalhe', nota.numero], {
      queryParams: {
        // Marca o detalhe como somente-leitura (NF ja entregue)
        readonly: 'true',
        // Chave
        filial: nota.filial,
        serie:  nota.serie,
        fornec: nota.fornecedorCod,
        loja:   nota.fornecedorLoja,
        // Tipo (CTe/NFSe): sem isso o detalhe assume NFSe (isCte=false) e a
        // TES/produto de CTe nao casam com as opcoes de NFSe -> campos vazios.
        tipo:   nota.tipo,
        // Emissor
        emissao:               nota.emissao,
        fornecedor:            nota.fornecedor,
        fornecedorCnpj:        nota.fornecedorCnpj,
        fornecedorFantasia:    nota.fornecedorFantasia,
        fornecedorEndereco:    nota.fornecedorEndereco,
        fornecedorBairro:      nota.fornecedorBairro,
        fornecedorMunicipio:   nota.fornecedorMunicipio,
        fornecedorEstado:      nota.fornecedorEstado,
        fornecedorCep:         nota.fornecedorCep,
        fornecedorComplemento: nota.fornecedorComplemento,
        fornecedorPais:        nota.fornecedorPais,
        // Tomador
        tomadorRazaoSocial: nota.tomadorRazaoSocial,
        tomadorFantasia:    nota.tomadorFantasia,
        tomadorEndereco:    nota.tomadorEndereco,
        tomadorBairro:      nota.tomadorBairro,
        tomadorMunicipio:   nota.tomadorMunicipio,
        tomadorEstado:      nota.tomadorEstado,
        tomadorCep:         nota.tomadorCep,
        tomadorCnpj:        nota.tomadorCnpj,
        tomadorPais:        nota.tomadorPais,
        tomadorComplemento: nota.tomadorComplemento,
        // Impostos
        impostoPIS:    nota.impostoPIS,
        impostoCOFINS: nota.impostoCOFINS,
        impostoIR:     nota.impostoIR,
        impostoINSS:   nota.impostoINSS,
        impostoCSLL:   nota.impostoCSLL,
        total:         nota.total,
        // Formulario (campos editaveis no detalhe)
        processo:      nota.processo,
        fin:           nota.fin,
        tes:           nota.tes,
        produto:       nota.produto,
        vencimento:    nota.vencimento
      }
    });
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  advancedFilterAction(): void {
    this.slideFilterAdvanced.open();
  }

  private buildFilterForm(): FormGroup {
    return this.fb.group({
      numero: [''],
      rps: [''],
      tipo: [''],
      fornecedor: [''],
      tomadorCnpj: [''],
      status: [''],
      usuario: [[]],
      codigo: [''],
      processo: [''],
      produto: [''],
      tes: [''],
      fin: [''],
      origem: [''],
      oculto: [''],
      semAnexo: [''],
      cnxStatus: [[]],
      emissaoDe: [''],
      emissaoAte: [''],
      recebidaDe: [''],
      recebidaAte: [''],
      aprovadoDe: [''],
      aprovadoAte: [''],
      vencimentoDe: [''],
      vencimentoAte: [''],
      totalDe: [null],
      totalAte: [null]
    });
  }

  confirmAdvancedFilter(): void {
    const v = this.filterForm.value;
    // tomadorCnpj guarda o CNPJ do tomador escolhido; o backend espera o
    // codigo da filial Protheus (SF1.F1_FILIAL), entao resolvemos via
    // FilialService.codigoByCnpj antes de enviar.
    const filialCod = v.tomadorCnpj ? this.filialService.codigoByCnpj(v.tomadorCnpj) : '';
    // Monta o objeto de filtros que vai pro backend. Strings/numeros vazios
    // viram undefined pra nao virarem query-string vazia desnecessaria.
    this.filtrosAtivos = {
      numero:        v.numero        || undefined,
      rps:           v.rps           || undefined,
      fornecedor:    v.fornecedor    || undefined,
      filial:        filialCod       || undefined,
      tipo:          v.tipo          || undefined,
      origem:        v.origem        || undefined,
      fin:           v.fin           || undefined,
      oculto:        v.oculto        || undefined,
      semAnexo:      v.semAnexo      || undefined,
      // cnxStatus eh multiselect (lista de estados da integracao Conexos).
      cnxStatus:     (Array.isArray(v.cnxStatus) && v.cnxStatus.length)
                       ? v.cnxStatus.join(',')
                       : undefined,
      processo:      v.processo      || undefined,
      produto:       v.produto       || undefined,
      tes:           v.tes           || undefined,
      // usuario eh multiselect (array de ZOA_COD). Envia como lista separada
      // por virgula; backend filtra F1_ZUSRPN IN (...). Vazio -> undefined.
      usuario:       (Array.isArray(v.usuario) && v.usuario.length)
                       ? v.usuario.join(',')
                       : undefined,
      codigo:        v.codigo        || undefined,
      emissaoDe:     v.emissaoDe     || undefined,
      emissaoAte:    v.emissaoAte    || undefined,
      recebidaDe:    v.recebidaDe    || undefined,
      recebidaAte:   v.recebidaAte   || undefined,
      aprovadoDe:    v.aprovadoDe    || undefined,
      aprovadoAte:   v.aprovadoAte   || undefined,
      vencimentoDe:  v.vencimentoDe  || undefined,
      vencimentoAte: v.vencimentoAte || undefined,
      totalDe:       v.totalDe       ?? undefined,
      totalAte:      v.totalAte      ?? undefined
    };
    // status: front so' tem 'Aprovado' como valor util, ignorado no backend.
    this.slideFilterAdvanced.close();
    this.carregar();
  }

  cancelAdvancedFilter(): void {
    this.slideFilterAdvanced.close();
  }

  clearAdvancedFilter(): void {
    this.filterForm.reset(this.buildFilterForm().value);
    this.hasActiveFilters = false;
    this.filtrosAtivos = {};
    this.carregar();
  }

  private filterAction(filter: string): void {
    // Pesquisa simples na barra superior. O termo eh aplicado no backend com
    // LIKE (OR) em F1_DOC, F1_NUMRPS e F1_NFELETR, via parametro 'busca'.
    const term = (filter || '').trim();
    this.filtrosAtivos = {
      ...this.filtrosAtivos,
      busca: term || undefined
    };
    this.carregar();
  }
}
